import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
} from '@iconik-io/core/src/date/date_formats';
import { normalizeDateFormat } from '@iconik-io/core/src/date/normalize_date_format';
import type { StatsBillingStatsSchemaNullable } from '@iconik-io/iconik-api/src/services/stats';

import {
  isSystemDomainStatus,
  isSystemDomainType,
  SYSTEM_DOMAIN_STATUSES,
  type SystemDomainStatus,
} from '../../constants/system_domains';
import { logout } from '../auth/slice';
import { billingInfoUpdated } from '../billing_information/slice';
import type { GettingStartedCheck } from '../getting_started_checklist/types';
import type {
  AppAppState,
  AssetSection,
  DifferentSystemDomain,
  DifferentUser,
  SearchSection,
  SessionAjaxConfig,
} from './types';

const storedDomainStatus = localStorage.getItem('SYSTEM_DOMAIN_STATUS');
const systemDomainStatus =
  storedDomainStatus && isSystemDomainStatus(storedDomainStatus)
    ? storedDomainStatus
    : undefined;

const initialState: AppAppState = {
  host: '',
  logo: '',
  iconikLogo: '',
  dateFormat: DEFAULT_DATE_FORMAT,
  dateTimeFormat: DEFAULT_DATE_TIME_FORMAT,
  assetDefaultSections: [],
  searchDefaultSections: [],
  sessionAjaxConfig: {},
  systemDomainStatus: systemDomainStatus || SYSTEM_DOMAIN_STATUSES.ACTIVE,
  systemDomainWarningMessage: '',
  systemDomainBalance: 0,
  systemStatusLoaded: false,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setHost: (state, action: PayloadAction<string>) => {
      state.host = action.payload;
    },

    setSessionAjaxConfig: (state, action: PayloadAction<SessionAjaxConfig>) => {
      state.sessionAjaxConfig = action.payload;
    },

    // ensure correct format. remove this when date formats are migrated
    setDateFormat: (state, action: PayloadAction<string>) => {
      state.dateFormat = normalizeDateFormat(action.payload);
    },

    setDateTimeFormat: (state, action: PayloadAction<string>) => {
      state.dateTimeFormat = normalizeDateFormat(action.payload);
    },

    refreshAppState: (state) => {
      state.appStateKey = `key-${Date.now()}`;
    },

    setLogo: (state, action: PayloadAction<string>) => {
      state.logo = action.payload;
    },

    setSystemDomainStatus: (
      state,
      action: PayloadAction<SystemDomainStatus | undefined>,
    ) => {
      state.systemDomainStatus =
        action.payload ?? SYSTEM_DOMAIN_STATUSES.ACTIVE;
    },

    setDifferentSystemDomain: (
      state,
      action: PayloadAction<DifferentSystemDomain>,
    ) => {
      state.differentSystemDomain = action.payload;
    },

    setDifferentUser: (state, action: PayloadAction<DifferentUser>) => {
      state.differentUser = action.payload;
    },

    setSearchDefaultPanels: (state, action: PayloadAction<SearchSection[]>) => {
      state.searchDefaultSections = action.payload;
    },

    setAssetDefaultPanels: (state, action: PayloadAction<AssetSection[]>) => {
      state.assetDefaultSections = action.payload;
    },

    setSystemStatus: (
      state,
      action: PayloadAction<StatsBillingStatsSchemaNullable>,
    ) => {
      state.systemStatusLoaded = true;
      state.stripeConfigured = Boolean(action.payload.stripe_id);
      state.systemDomainBalance = action.payload.current_balance ?? 0;
      const { system_domain_type, system_domain_status } = action.payload;
      state.systemDomainType =
        system_domain_type && isSystemDomainType(system_domain_type)
          ? system_domain_type
          : undefined;
      state.systemDomainStatus =
        system_domain_status && isSystemDomainStatus(system_domain_status)
          ? system_domain_status
          : undefined;
      state.systemDomainWarningMessage =
        action.payload.system_domain_warning_message;
      state.systemDomainInvoiceEndOfMonth =
        action.payload.invoice_end_of_month ?? undefined;
    },

    clearDifferentSystemDomain: (state) => {
      state.differentSystemDomain = undefined;
      state.systemStatusLoaded = false;
      state.systemDomainBalance = 0;
      state.systemDomainType = undefined;
      state.systemDomainStatus = SYSTEM_DOMAIN_STATUSES.ACTIVE;
    },

    clearDifferentUser: () => {},

    setGettingStartedCheck: (
      _state,
      _action: PayloadAction<GettingStartedCheck>,
    ) => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(billingInfoUpdated, (state) => {
        state.stripeConfigured = true;
      })
      .addCase(logout, (state) => {
        state.differentSystemDomain = undefined;
        state.systemStatusLoaded = false;
        state.systemDomainBalance = 0;
        state.systemDomainType = undefined;
        state.systemDomainStatus = SYSTEM_DOMAIN_STATUSES.ACTIVE;
      });
  },
});

export const {
  setHost,
  setSessionAjaxConfig,
  setDateFormat,
  setDateTimeFormat,
  refreshAppState,
  setLogo,
  setSystemDomainStatus,
  setDifferentSystemDomain,
  setDifferentUser,
  setSearchDefaultPanels,
  setAssetDefaultPanels,
  setSystemStatus,
  clearDifferentSystemDomain,
  clearDifferentUser,
  setGettingStartedCheck,
} = appSlice.actions;

export const appReducer = appSlice.reducer;
